import { useEffect, useState } from "react";
import { useIntl } from 'react-intl';
import { Col, Row } from "reactstrap";
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import Toast from '../../helpers/Toast';
import { AssistanceDTO } from '../../models/Assistance/AssistanceDTO';
import { AssistanceLoanEquipmentDTO } from '../../models/Assistance/AssistanceLoanEquipmentDTO';
import { EditAssistanceLoanEquipmentDTO } from '../../models/Assistance/EditAssistanceLoanEquipmentDTO';
import { AssistanceService } from '../../services/AssistanceService';
import { LoanEquipmentService } from '../../services/LoanEquipmentService';
import { Utilities } from '../../helpers/Utilities';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import SearchSelectComponent, { SelectItemsProps } from '../SearchSelectComponent';

export type AssistanceLoanEquipmentProps = {
    assistance: AssistanceDTO | null;
    isComplete: boolean;
}

const AssistanceLoanEquipment = ({ assistance, isComplete }: AssistanceLoanEquipmentProps) => {
    const intl = useIntl();
    const assistanceService = new AssistanceService();
    const loanEquipmentService = new LoanEquipmentService();
    const [loanEquipmentFromAssistance, setLoanEquipmentFromAssistance] = useState<AssistanceLoanEquipmentDTO>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditButtonLoading, setIsEditButtonLoading] = useState<boolean>(false);

    const { availableModules } = useAvailableModulesContext();

    const GetLoanEquipmentByAssistanceId = async () => {
        setIsLoading(true);
        var response = await assistanceService.GetAssistanceLoanEquipment(assistance?.id ?? 0);
        if (response.success === false || response.obj == null) {
            setIsLoading(false);
            return;
        }
        setLoanEquipmentFromAssistance(response.obj);
        setIsLoading(false);
    };

    useEffect(() => {
        if (assistance?.id) {
            GetLoanEquipmentByAssistanceId();
        }
    }, [assistance?.id]);

    const editAssistanceLoanEquipment = async () => {
        setIsEditButtonLoading(true);

        const updatedAssistanceLoanEquipment: EditAssistanceLoanEquipmentDTO = {
            assistanceId: assistance?.id ?? 0,
            loanEquipmentId: loanEquipmentFromAssistance?.id ?? null,
            dateDeliveredLoanEquipment: loanEquipmentFromAssistance?.dateDeliveredLoanEquipment ?? null,
            datePickedupLoanEquipment: loanEquipmentFromAssistance?.datePickedupLoanEquipment ?? null,
        };

        const response = await assistanceService.EditAssistanceLoanEquipment(updatedAssistanceLoanEquipment);
        setIsEditButtonLoading(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }
        Toast.Show("success", response.message);
    };

    const GetLoanEquipmentOptions = async (value: string): Promise<SelectItemsProps[]> => {
        try {
            const nameFilter = value && value.trim().length > 0 ? value : undefined;
            const equipments = await loanEquipmentService.getAvailable(nameFilter);

            const list: SelectItemsProps[] = equipments.map((equipment) => ({
                value: equipment.id.toString(),
                label: equipment.name || "",
            }));

            const finalList = !nameFilter ? list.slice(0, 5) : list;

            return finalList;
        } catch (error) {
            Toast.Show("error", intl.formatMessage({ id: "Assistance.LoanEquipment.Equipment.Error" }));
            return [];
        }
    };

    return (
        <div>
            <Row style={{ margin: "1rem 0px" }}>
                <Row
                    style={{
                        margin: "0px",
                        padding: "0px",
                        alignItems: "flex-end",
                        alignContent: "flex-end",
                    }}
                >
                    <SearchSelectComponent
                        xl={12}
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        style={{ width: "100%" }}
                        label={intl.formatMessage({ id: "Assistance.LoanEquipment.Equipment" })}
                        value={loanEquipmentFromAssistance?.id && loanEquipmentFromAssistance?.name ? {
                            value: loanEquipmentFromAssistance.id.toString(),
                            label: loanEquipmentFromAssistance.name
                        } : null}
                        onChange={(selected) => {
                            setLoanEquipmentFromAssistance({
                                ...loanEquipmentFromAssistance!,
                                id: selected ? parseInt(selected.value) : null,
                                name: selected ? selected.label : null
                            });
                        }}
                        loadOptions={GetLoanEquipmentOptions}
                        isDisabled={isLoading || isEditButtonLoading || isComplete}
                    />
                </Row>

                <Row
                    style={{
                        margin: "0px",
                        padding: "0px",
                        alignItems: "flex-end",
                        alignContent: "flex-end",
                    }}
                >
                    <InputComponent
                        xl={6}
                        lg={6}
                        md={6}
                        sm={6}
                        xs={12}
                        style={{ width: "100%" }}
                        type="date"
                        value={loanEquipmentFromAssistance?.dateDeliveredLoanEquipment}
                        label={intl.formatMessage({ id: "Assistance.LoanEquipment.DeliveryDate" })}
                        readOnly={isComplete}
                        onChange={(dateDeliveredLoanEquipment: Date) => {
                            setLoanEquipmentFromAssistance({
                                ...loanEquipmentFromAssistance!,
                                dateDeliveredLoanEquipment: dateDeliveredLoanEquipment
                            });
                        }}
                        showLoadingSkeleton={isLoading || isEditButtonLoading}
                    />

                    <InputComponent
                        xl={6}
                        lg={6}
                        md={6}
                        sm={6}
                        xs={12}
                        style={{ width: "100%" }}
                        type="date"
                        value={loanEquipmentFromAssistance?.datePickedupLoanEquipment}
                        label={intl.formatMessage({ id: "Assistance.LoanEquipment.PickupDate" })}
                        readOnly={false}
                        onChange={(datePickedupLoanEquipment: Date) => {
                            setLoanEquipmentFromAssistance({
                                ...loanEquipmentFromAssistance!,
                                datePickedupLoanEquipment: datePickedupLoanEquipment
                            });
                        }}
                        showLoadingSkeleton={isLoading || isEditButtonLoading}
                    />
                </Row>
            </Row>
            <Row style={{ margin: "0px", padding: "0px", justifyContent: "end" }}>
                <Col xl={3} lg={3} md={3} sm={12} xs={12}>
                    <ButtonComponent
                        style={{ width: "100%", marginTop: "10px" }}
                        text={intl.formatMessage({ id: "AssistanceEditA.Save" })}
                        onClick={() => editAssistanceLoanEquipment()}
                        disabled={isLoading || isEditButtonLoading}
                    />
                </Col>
            </Row>
        </div>
    );
};

export { AssistanceLoanEquipment };
